import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  SecurityContext,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { CitationWithId } from '@sciflow/cite';
import { Reference } from 'projects/reference-management/src/lib/reference.model';
import {
  BibliographyGeneratorDialogComponent,
  BibliographyGeneratorDialogResult,
} from 'projects/reference-management/src/lib/references/bibliography-generator-dialog/bibliography-generator-dialog.component';
import { ReferenceManagementService } from 'projects/reference-management/src/lib/services/reference-management.service';
import {
  removeReference,
  setActiveReferenceId,
  setReferenceSortOrder,
  updateReferenceRawString,
} from 'projects/reference-management/src/lib/store/references.actions';
import {
  ReferenceSortOrder,
  selectAreAllReferencesCited,
  selectCitationlist,
  selectReferenceList,
  selectReferenceSortOrder,
} from 'projects/reference-management/src/lib/store/references.reducer';
import { combineLatest, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { updateDocumentState } from '../../editor.actions';
import { selectActiveInstance } from '../../editor.reducer';
import {
  buildCitationOccurrencesByReferenceId,
  buildReferenceAppearanceIndexById,
} from '../../prosemirror/views/citation-occurrences';
import {
  BibliographyReferenceOccurrenceVm,
  BibliographyReferenceRowVm,
  ReferenceRowActionEventDetail,
} from '../../prosemirror/views/reference-table.models';
import {
  buildBibliographyReferenceRow,
  getReferenceIds,
} from '../../prosemirror/views/reference-table.utils';
import { inferPromotedReferenceFields } from '../../prosemirror/quick-actions/quick-actions-menu/reference-promotion-parser';
import { sortReferences } from 'projects/reference-management/src/lib/references/reference-sort.utils';

@Component({
  selector: 'sfo-bibliography-generator',
  templateUrl: './bibliography-generator.component.html',
  styleUrls: ['./bibliography-generator.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatCardModule,
    MatTooltipModule,
  ],
})
export class BibliographyGeneratorComponent implements OnInit, OnDestroy {
  rows: BibliographyReferenceRowVm[] = [];
  showEdit = true;
  sortOrder = toSignal(this.store.select(selectReferenceSortOrder), {
    initialValue: 'none' as const,
  });
  allCslReferencesCited = toSignal(this.store.select(selectAreAllReferencesCited), {
    initialValue: false,
  });
  private referencesByKey = new Map<string, Reference>();

  @Output('reference-row-action')
  referenceRowAction = new EventEmitter<ReferenceRowActionEventDetail>();
  @Output('cited-in-click')
  citedInClick = new EventEmitter<{ partId: string; title: string; refId?: string }>();

  private stop$ = new Subject<void>();

  constructor(
    private store: Store,
    private dialog: MatDialog,
    private changeDetector: ChangeDetectorRef,
    private referenceManagementService: ReferenceManagementService,
    private sanitizer: DomSanitizer,
  ) {}

  /**
   * Subscribes to reducer-backed bibliography rows and citation state.
   */
  ngOnInit(): void {
    combineLatest([
      this.store.select(selectReferenceList),
      this.store.select(selectCitationlist),
      this.store.select(selectActiveInstance),
      this.store.select(selectReferenceSortOrder),
    ])
      .pipe(
        map(([references, citations, activeInstance, sortOrder]) =>
          this.buildRows(references || [], citations || [], activeInstance?.state?.doc, sortOrder),
        ),
        takeUntil(this.stop$),
      )
      .subscribe((rows) => {
        this.rows = rows;
        this.changeDetector.markForCheck();
      });
  }

  /**
   * Cleans up subscriptions.
   */
  ngOnDestroy(): void {
    this.stop$.next();
    this.stop$.complete();
  }

  /**
   * Returns the display label for chapters where a reference is cited.
   */
  getCitedInLabel(row: BibliographyReferenceRowVm): string {
    if (!row.citedIn || row.citedIn.length === 0) {
      return 'Not cited';
    }

    return row.citedIn.map((occurrence) => occurrence.title).join(', ');
  }

  /**
   * Emits chapter-navigation events for clicked cited-in labels.
   */
  onCitedInClick(
    row: BibliographyReferenceRowVm,
    occurrence: BibliographyReferenceOccurrenceVm,
    event: MouseEvent,
  ): void {
    event.preventDefault();
    event.stopPropagation();
    this.citedInClick.emit({
      partId: occurrence.partId,
      title: occurrence.title,
      refId: row.refId,
    });
  }

  /**
   * Handles keyboard activation of a row (Enter or Space) for accessibility.
   */
  onRowKeydown(event: KeyboardEvent, row: BibliographyReferenceRowVm): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) {
      return;
    }
    event.preventDefault();
    if (event.key === ' ') {
      event.stopPropagation();
    }
    this.onActivate(row, event as unknown as MouseEvent);
  }

  /**
   * Opens the export-format dialog for the clicked row.
   */
  onActivate(row: BibliographyReferenceRowVm, event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (target?.closest('button')) {
      return;
    }

    this.referenceRowAction.emit({
      action: 'activate',
      rowIndex: row.rowIndex,
      refId: row.refId,
    });

    if (row.refId) {
      this.store.dispatch(setActiveReferenceId({ referenceId: row.refId }));
    }

    const reference = this.referencesByKey.get(row.referenceKey) ?? {
      key: row.referenceKey,
      csl: {
        id: row.refId || row.referenceKey,
        rawCitation: row.customText || row.title,
      },
    };

    const dialogRef = this.dialog.open(BibliographyGeneratorDialogComponent, {
      width: '80vw',
      maxWidth: '80vw',
      data: {
        reference,
        exportFormat: row.exportFormat,
        canEdit: row.canEdit,
        isPlainReference: row.isPlainReference,
        isAiReference: row.source === 'ai',
      },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.stop$))
      .subscribe((result?: BibliographyGeneratorDialogResult) => {
        this.handleFormatDialogResult(result);
      });
  }

  /**
   * Sanitizes custom reference HTML before rendering.
   */
  sanitizeCustomText(value?: string): string {
    if (!value) {
      return '';
    }
    return this.sanitizer.sanitize(SecurityContext.HTML, value) || '';
  }

  private async handleFormatDialogResult(
    result: BibliographyGeneratorDialogResult | undefined,
  ): Promise<void> {
    if (!result?.key) {
      return;
    }

    if (result.exportFormat === 'auto') {
      this.store.dispatch(
        updateReferenceRawString({ key: result.key, rawCitationString: undefined }),
      );
    } else {
      this.store.dispatch(
        updateReferenceRawString({ key: result.key, rawCitationString: result.rawCitationString }),
      );
    }

    const nextReference = this.applyInferredFieldsForCustomFormat(result);
    if (nextReference) {
      await this.referenceManagementService.updateReference(nextReference);
      this.store.dispatch(updateDocumentState({ dirty: true }));
    }
  }

  /**
   * Re-infers key CSL fields from custom raw citation text to avoid stale metadata.
   */
  private applyInferredFieldsForCustomFormat(
    result: BibliographyGeneratorDialogResult,
  ): Reference | undefined {
    if (!result.updatedReference?.csl || result.exportFormat !== 'custom') {
      return result.updatedReference;
    }

    const rawCitation = result.rawCitationString?.trim() ?? '';
    const inferredFields = inferPromotedReferenceFields(rawCitation);
    return {
      ...result.updatedReference,
      csl: {
        ...result.updatedReference.csl,
        rawCitation: rawCitation || undefined,
        author: inferredFields.author,
        issued: inferredFields.issued,
      },
    };
  }

  /**
   * Handles row delete requests and removes the reference from reducer state.
   */
  onDelete(row: BibliographyReferenceRowVm, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (!row.refId) {
      return;
    }

    this.referenceRowAction.emit({
      action: 'delete',
      rowIndex: row.rowIndex,
      refId: row.refId,
    });

    this.store.dispatch(removeReference({ referenceId: row.refId }));
    this.store.dispatch(updateDocumentState({ dirty: true }));
  }

  /**
   * Updates bibliography sort order.
   */
  setSortOrder(sortOrder: ReferenceSortOrder): void {
    this.store.dispatch(setReferenceSortOrder({ sortOrder }));
  }

  /**
   * Maps missing metadata field keys to compact issue labels.
   */
  getMissingMetadataLabel(field: string): string {
    if (field === 'author') {
      return 'Missing author';
    }
    if (field === 'year') {
      return 'Missing year';
    }
    return `Missing ${field}`;
  }

  /**
   * Returns whether this reference may fail in author-date styles.
   */
  hasAuthorDateCompatibilityRisk(row: BibliographyReferenceRowVm): boolean {
    return row.missingMetadata.includes('author') || row.missingMetadata.includes('year');
  }

  /**
   * Builds table rows from ordered references and citation occurrences.
   */
  private buildRows(
    references: Reference[],
    citations: CitationWithId[],
    doc: Record<string, unknown> | undefined,
    sortOrder: ReferenceSortOrder,
  ): BibliographyReferenceRowVm[] {
    const appearanceIndexById = buildReferenceAppearanceIndexById(doc as any, citations);
    const sortedReferences = sortReferences(references, sortOrder, appearanceIndexById);
    const occurrencesByReferenceId = buildCitationOccurrencesByReferenceId(doc as any, citations);
    this.referencesByKey = new Map<string, Reference>();

    return sortedReferences.map((reference, index) => {
      const rowIndex = index + 1;
      const citedIn = this.resolveCitedInOccurrences(reference, occurrencesByReferenceId);
      this.referencesByKey.set(reference.key, reference);
      return buildBibliographyReferenceRow({
        reference,
        citedIn,
        rowIndex,
        showEdit: this.showEdit,
      });
    });
  }

  /**
   * Resolves chapter labels where a reference appears.
   */
  private resolveCitedInOccurrences(
    reference: Reference,
    occurrencesByReferenceId: Map<string, Array<{ partId: string; title: string }>>,
  ): BibliographyReferenceOccurrenceVm[] {
    const chapterOccurrences: BibliographyReferenceOccurrenceVm[] = [];
    const seen = new Set<string>();

    for (const referenceId of getReferenceIds(reference)) {
      for (const occurrence of occurrencesByReferenceId.get(referenceId) || []) {
        const partId = typeof occurrence?.partId === 'string' ? occurrence.partId.trim() : '';
        const title = typeof occurrence?.title === 'string' ? occurrence.title.trim() : '';
        const key = `${partId}::${title}`;
        if (partId && title && !seen.has(key)) {
          seen.add(key);
          chapterOccurrences.push({ partId, title });
        }
      }
    }

    return chapterOccurrences;
  }

}
